import type { DynamicFormSchema, FormField, FormFieldOption, FormSection } from '@/types/form'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

function formatFieldName(name: string, index?: number): string {
  if (index === undefined || !name?.includes?.('{{index}}')) return name
  return name.replace(/\{\{index\}\}/g, String(index))
}

function formatValue(val: any, field: FormField, index?: number): string {
  if (val === null || val === undefined || val === '') return '-'
  if (val instanceof File || val instanceof FileList) return 'Fichier joint'
  if (field.type === 'switch') return val ? 'Oui' : 'Non'
  if (field.type === 'date' && val) {
    try {
      const d = new Date(val)
      return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString('fr-FR')
    } catch {
      return String(val)
    }
  }
  if (field.type === 'select' && field.options && Array.isArray(field.options)) {
    const opts = field.options as FormFieldOption[]
    const arr = Array.isArray(val) ? val : [val]
    const labels = arr
      .map((v) => {
        const opt = opts.find((o) => o.value === v || o.value === String(v))
        return opt?.label ?? v
      })
      .filter(Boolean)
    return labels.length ? labels.join(', ') : String(val)
  }
  if (Array.isArray(val)) return val.map((v) => (typeof v === 'object' ? JSON.stringify(v) : v)).join(', ')
  if (typeof val === 'object') return JSON.stringify(val)
  if (typeof val === 'string' && val.startsWith('<')) {
    const div = typeof document !== 'undefined' ? document.createElement('div') : null
    if (div) {
      div.innerHTML = val
      return div.textContent || div.innerText || val
    }
    return val.replace(/<[^>]+>/g, '')
  }
  return String(val)
}

type PdfRow = { label: string; value: string } | { section: string }

function getFieldRow(
  field: FormField,
  model: Record<string, any>,
  index?: number,
): PdfRow {
  const name = formatFieldName(field.name, index)
  const resolvedName = field.id && field.repeatable ? formatFieldName(field.id, index) : name
  let val: any
  if (model[resolvedName] !== undefined) val = model[resolvedName]
  else if (model[name] !== undefined) val = model[name]
  else if (model[field.name] !== undefined) val = model[field.name]
  else val = undefined
  const display = formatValue(val, field, index)
  const label = field.label || field.name || name
  return { label, value: display }
}

function collectSectionRows(
  section: FormSection,
  model: Record<string, any>,
  path: string = '',
): PdfRow[] {
  const rows: PdfRow[] = []
  const sectionData = model[section.id]

  if (section.repeatable && Array.isArray(sectionData)) {
    sectionData.forEach((item: Record<string, any>, idx: number) => {
      rows.push({ section: `${section.name || section.id} (${idx + 1})` })
      ;(section.fields || []).forEach((field) => {
        if (field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields) {
          if (field.sections) field.sections.forEach((sub) => rows.push(...collectSectionRows(sub, item, `${path}.${section.id}[${idx}]`)))
          else if (field.fields) field.fields.forEach((sub) => rows.push(getFieldRow(sub, item, idx)))
        } else {
          rows.push(getFieldRow(field, item, idx))
        }
      })
    })
  } else {
    const ctx = sectionData && typeof sectionData === 'object' ? sectionData : model
    rows.push({ section: section.name || section.id })
    ;(section.fields || []).forEach((field) => {
      if (field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields) {
        if (field.repeatable && ctx[field.id]) {
          const items = Array.isArray(ctx[field.id]) ? ctx[field.id] : []
          items.forEach((subItem: Record<string, any>, idx: number) => {
            ;(field.fields || []).forEach((sub) => rows.push(getFieldRow(sub, subItem, idx)))
          })
        } else if (field.sections) field.sections.forEach((sub) => rows.push(...collectSectionRows(sub, ctx, path)))
        else if (field.fields) field.fields.forEach((sub) => rows.push(getFieldRow(sub, ctx)))
      } else {
        rows.push(getFieldRow(field, ctx))
      }
    })
  }
  if (section.sections?.length) {
    const ctx = sectionData && typeof sectionData === 'object' ? sectionData : model
    section.sections.forEach((sub) => rows.push(...collectSectionRows(sub, ctx, path)))
  }
  return rows
}

function renderField(
  field: FormField,
  model: Record<string, any>,
  index?: number,
): string {
  const name = formatFieldName(field.name, index)
  const resolvedName = field.id && field.repeatable ? formatFieldName(field.id, index) : name
  let val: any
  if (model[resolvedName] !== undefined) {
    val = model[resolvedName]
  } else if (model[name] !== undefined) {
    val = model[name]
  } else if (model[field.name] !== undefined) {
    val = model[field.name]
  } else {
    val = undefined
  }
  const display = formatValue(val, field, index)
  const label = field.label || field.name || name
  return `<tr><td style="padding:4px 8px;border-bottom:1px solid #e5e7eb;font-weight:500;width:40%">${escapeHtml(label)}</td><td style="padding:4px 8px;border-bottom:1px solid #e5e7eb">${escapeHtml(display)}</td></tr>`
}

function escapeHtml(s: string): string {
  const div = typeof document !== 'undefined' ? document.createElement('div') : null
  if (div) {
    div.textContent = s
    return div.innerHTML
  }
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function renderSection(
  section: FormSection,
  model: Record<string, any>,
  path: string = '',
): string {
  const rows: string[] = []
  const sectionData = model[section.id]

  if (section.repeatable && Array.isArray(sectionData)) {
    sectionData.forEach((item: Record<string, any>, idx: number) => {
      rows.push(`<tr><td colspan="2" style="padding:8px 0 4px;font-weight:600;color:#374151">${escapeHtml(section.name || section.id)} (${idx + 1})</td></tr>`)
      ;(section.fields || []).forEach((field) => {
        if (field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields) {
          if (field.sections) {
            field.sections.forEach((sub) => {
              rows.push(renderSection(sub, item, `${path}.${section.id}[${idx}]`))
            })
          }
          if (field.fields) {
            field.fields.forEach((sub) => {
              rows.push(renderField(sub, item, idx))
            })
          }
        } else {
          rows.push(renderField(field, item, idx))
        }
      })
    })
  } else {
    const ctx = sectionData && typeof sectionData === 'object' ? sectionData : model
    rows.push(`<tr><td colspan="2" style="padding:8px 0 4px;font-weight:600;color:#374151">${escapeHtml(section.name || section.id)}</td></tr>`)
    ;(section.fields || []).forEach((field) => {
      if (field.type === 'section' || field.type === 'under-section' || field.repeatable || field.fields) {
        if (field.repeatable && ctx[field.id]) {
          const items = Array.isArray(ctx[field.id]) ? ctx[field.id] : []
          items.forEach((subItem: Record<string, any>, idx: number) => {
            ;(field.fields || []).forEach((sub) => {
              rows.push(renderField(sub, subItem, idx))
            })
          })
        } else if (field.sections) {
          field.sections.forEach((sub) => rows.push(renderSection(sub, ctx, path)))
        } else if (field.fields) {
          field.fields.forEach((sub) => rows.push(renderField(sub, ctx)))
        }
      } else {
        rows.push(renderField(field, ctx))
      }
    })
  }

  if (section.sections?.length) {
    const ctx = sectionData && typeof sectionData === 'object' ? sectionData : model
    section.sections.forEach((sub) => {
      rows.push(renderSection(sub, ctx, path))
    })
  }

  return rows.join('')
}

export function generateFormPdf(
  schema: DynamicFormSchema,
  formData: Record<string, any>,
  options: FormToPdfOptions & { filename: string },
): void {
  const {
    formName,
    quoteRef,
    companyName,
    createdAt,
    commercial,
    commercialEmail,
    iav,
    iavEmail,
    totalCA,
    margeTotal,
    margeCompteur,
    signedAt,
    duration,
    modePaiement,
    orderType,
    siteFacturation,
    siteLivraison,
    orderComments,
    filename,
  } = options
  const doc = new jsPDF()
  const margin = 14
  let y = 20

  doc.setFontSize(16)
  doc.text(formName || schema.name || 'Formulaire', margin, y)
  y += 12

  doc.setFontSize(10)
  doc.setTextColor(107, 114, 128)
  const metaLines: string[] = []
  if (quoteRef) metaLines.push(`Devis : ${quoteRef}`)
  if (companyName) metaLines.push(`Client : ${companyName}`)
  if (commercial) metaLines.push(`Commercial : ${commercial}${commercialEmail ? ` (${commercialEmail})` : ''}`)
  if (iav) metaLines.push(`IAV : ${iav}${iavEmail ? ` (${iavEmail})` : ''}`)
  if (totalCA != null) metaLines.push(`Total CA : ${totalCA} €`)
  if (margeTotal != null) metaLines.push(`Marge total : ${margeTotal} €`)
  if (margeCompteur != null) metaLines.push(`Marge compteur : ${margeCompteur} €`)
  if (signedAt) metaLines.push(`Signé le : ${signedAt}`)
  if (duration) metaLines.push(`Durée : ${duration}`)
  if (modePaiement) metaLines.push(`Mode paiement : ${modePaiement}`)
  if (orderType) metaLines.push(`Type commande : ${orderType}`)
  if (siteFacturation?.name || siteFacturation?.address) metaLines.push(`Site facturation : ${[siteFacturation.name, siteFacturation.address].filter(Boolean).join(' - ')}`)
  if (siteLivraison?.name || siteLivraison?.address) metaLines.push(`Site livraison : ${[siteLivraison.name, siteLivraison.address].filter(Boolean).join(' - ')}`)
  if (createdAt) metaLines.push(`Généré le : ${createdAt}`)
  metaLines.forEach((line) => {
    doc.text(line, margin, y)
    y += 6
  })
  y += 4
  doc.setTextColor(31, 41, 55)

  const allRows: PdfRow[] = []
  ;(schema.sections || []).forEach((sec) => allRows.push(...collectSectionRows(sec, formData)))

  const tableData: string[][] = []
  for (const row of allRows) {
    if ('section' in row) {
      tableData.push([row.section, ''])
    } else {
      tableData.push([row.label, row.value])
    }
  }

  const pageWidth = doc.internal.pageSize.getWidth()
  const tableWidth = pageWidth - 2 * margin

  if (tableData.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Champ', 'Valeur']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [55, 65, 81], fontStyle: 'bold' },
      styles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 68 }, 1: { cellWidth: tableWidth - 68 } },
      margin: { left: margin, right: margin },
    })
    const lastTable = (doc as { lastAutoTable?: { finalY: number } }).lastAutoTable
    y = (lastTable?.finalY ?? y) + 10
  }

  if (orderComments && orderComments.length > 0) {
    const commentRows: string[][] = []
    for (const c of orderComments) {
      const authorLabel = c.author?.firstName || c.author?.lastName
        ? [c.author.firstName, c.author.lastName].filter(Boolean).join(' ')
        : c.author?.email || '-'
      const dateStr = c.createdAt
        ? new Date(c.createdAt).toLocaleString('fr-FR')
        : '-'
      const text = (c.comment || c.content || c.message || '').trim()
      const plainText =
        typeof text === 'string' && text.startsWith('<')
          ? text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
          : text
      commentRows.push([authorLabel, dateStr, (plainText || '-').slice(0, 500)])
    }
    autoTable(doc, {
      startY: y,
      head: [['Auteur', 'Date', 'Commentaire']],
      body: commentRows,
      theme: 'grid',
      headStyles: { fillColor: [55, 65, 81], fontStyle: 'bold' },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 38 },
        1: { cellWidth: 34 },
        2: { cellWidth: tableWidth - 72 },
      },
      margin: { left: margin, right: margin },
    })
  }

  doc.save(filename)
}

interface OrderCommentPdf {
  author?: { firstName?: string; lastName?: string; email?: string }
  createdAt?: string | Date
  comment?: string
  content?: string
  message?: string
}

export interface FormToPdfOptions {
  formName?: string
  quoteRef?: string
  companyName?: string
  createdAt?: string
  commercial?: string
  commercialEmail?: string
  iav?: string
  iavEmail?: string
  totalCA?: string | number
  margeTotal?: string | number
  margeCompteur?: string | number
  signedAt?: string
  duration?: string
  modePaiement?: string
  orderType?: string
  siteFacturation?: { name?: string; address?: string }
  siteLivraison?: { name?: string; address?: string }
  orderComments?: OrderCommentPdf[]
}
